import { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../../../shadcn/ui/Dialog";
import { Button } from "../../../shadcn/ui/Button";
import { Star } from "lucide-react";

interface QuoteRatingDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onRate: (rating: number) => Promise<void>;
  quoteId: string;
  sellerName?: string;
}

export const QuoteRatingDialog = ({
  open,
  onOpenChange,
  onRate,
  quoteId,
  sellerName,
}: QuoteRatingDialogProps) => {
  const [rating, setRating] = useState(0);
  const [hoverRating, setHoverRating] = useState(0);
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async () => {
    if (rating < 1 || rating > 5) {
      return;
    }
    try {
      setSubmitting(true);
      await onRate(rating);
      setRating(0);
      setHoverRating(0);
      onOpenChange(false);
    } catch (error) {
      console.error("Error rating quote:", error);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Rate Quote</DialogTitle>
          <DialogDescription>
            {sellerName
              ? `Rate the quote from ${sellerName}`
              : "Rate this quote (1-5 stars)"}
          </DialogDescription>
        </DialogHeader>
        <div className="py-4">
          <div className="flex gap-2 justify-center mb-4">
            {[1, 2, 3, 4, 5].map((star) => {
              const isActive =
                hoverRating >= star || (!hoverRating && rating >= star);

              return (
                <button
                  key={star}
                  type="button"
                  onClick={() => setRating(star)}
                  onMouseEnter={() => setHoverRating(star)}
                  onMouseLeave={() => setHoverRating(0)}
                  className={`w-10 h-10 border rounded-md flex items-center justify-center transition-colors ${
                    isActive
                      ? "bg-yellow-50 border-yellow-300"
                      : "bg-white border-gray-300"
                  }`}
                  disabled={submitting}
                >
                  <Star
                    className={`w-6 h-6 ${
                      isActive ? "text-yellow-400 fill-yellow-400" : "text-gray-300"
                    }`}
                  />
                </button>
              );
            })}
          </div>
          {rating > 0 && (
            <p className="text-center text-sm text-muted-foreground">
              {rating} star{rating !== 1 ? "s" : ""} selected
            </p>
          )}
        </div>
        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={submitting}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={rating < 1 || rating > 5 || submitting}
            variant="primary"
          >
            {submitting ? "Submitting..." : "Submit Rating"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default QuoteRatingDialog;

